import { TrackingJourney } from 'types/tracking';

/** Parse date từ API "28-01-2026 14:02:51" → "28/01/2026 – 14:02" */
export function formatTrackingDate(dateStr: string): string {
  if (!dateStr) return '';
  // API format: DD-MM-YYYY HH:mm:ss
  const parts = dateStr.trim().split(' ');
  if (parts.length < 2) return dateStr;
  const [datePart, timePart] = parts;
  const [day, month, year] = datePart.split('-');
  const [hour, minute] = timePart.split(':');
  if (!day || !month || !year || !hour || !minute) return dateStr;
  return `${day}/${month}/${year} – ${hour}:${minute}`;
}

/** Convert gram → kg với 2 chữ số thập phân */
export function formatWeight(grams: number): string {
  if (grams == null || isNaN(grams)) return '—';
  return `${(grams / 1000).toFixed(2)} kg`;
}

/** Format VND currency */
export function formatCurrency(vnd: number): string {
  if (vnd == null || isNaN(vnd)) return '—';
  return vnd.toLocaleString('vi-VN') + 'đ';
}

/** Lấy label chính của 1 journey event: ưu tiên tenTrangThai → ghiChu → noiDung */
export function getJourneyLabel(journey: TrackingJourney): string {
  if (journey.tenTrangThai && journey.tenTrangThai.trim()) return journey.tenTrangThai.trim();
  if (journey.ghiChu && journey.ghiChu.trim()) return journey.ghiChu.trim();
  if (journey.noiDung && journey.noiDung.trim()) return journey.noiDung.trim();
  return 'Cập nhật trạng thái';
}

export type StatusVariant = 'delivered' | 'transit' | 'pending' | 'error' | 'default';

const STATUS_MAP: Record<number, { label: string; variant: StatusVariant }> = {
  100: { label: 'Tạo đơn / Duyệt đơn', variant: 'pending' },
  107: { label: 'Hủy đơn', variant: 'error' },
  200: { label: 'Đóng bảng kê', variant: 'pending' },
  300: { label: 'Tạo bàn giao', variant: 'pending' },
  400: { label: 'Nhận bàn giao', variant: 'pending' },
  500: { label: 'Giao bưu tá đi phát', variant: 'transit' },
  501: { label: 'Giao thành công', variant: 'delivered' },
  502: { label: 'Chuyển hoàn', variant: 'error' },
  504: { label: 'Duyệt hoàn', variant: 'error' },
  505: { label: 'Bưu cục lưu kho', variant: 'transit' },
  508: { label: 'Phát tiếp', variant: 'transit' },
  509: { label: 'Chuyển tiếp', variant: 'transit' },
  510: { label: 'Giao một phần', variant: 'transit' },
  515: { label: 'Duyệt hoàn', variant: 'error' },
  550: { label: 'Duyệt hoàn', variant: 'error' },
};

const VARIANT_COLOR: Record<StatusVariant, string> = {
  delivered: '#10b981',
  transit: '#f59e0b',
  pending: '#00ADEF',
  error: '#ef4444',
  default: '#94a3b8',
};

/** Map status code → { label, color, variant } */
export function mapStatusMeta(
  status: number,
  journeys: TrackingJourney[],
  statusName: string | null,
): { label: string; color: string; variant: StatusVariant } {
  // Ưu tiên status_name từ root data
  if (statusName && statusName.trim()) {
    const mapped = STATUS_MAP[status];
    return {
      label: statusName.trim(),
      color: VARIANT_COLOR[mapped?.variant ?? 'transit'],
      variant: mapped?.variant ?? 'transit',
    };
  }

  // Map theo status code
  const mapped = STATUS_MAP[status];
  if (mapped) {
    return {
      label: mapped.label,
      color: VARIANT_COLOR[mapped.variant],
      variant: mapped.variant,
    };
  }

  // Fallback: dùng tenTrangThai từ journey mới nhất
  const latest = journeys[0];
  if (latest?.tenTrangThai) {
    return { label: latest.tenTrangThai, color: VARIANT_COLOR.transit, variant: 'transit' };
  }
  if (latest?.ghiChu) {
    return { label: latest.ghiChu, color: VARIANT_COLOR.transit, variant: 'transit' };
  }

  return { label: 'Chưa có cập nhật', color: VARIANT_COLOR.default, variant: 'default' };
}

/** Lấy mô tả phụ của journey (ghi chú + bưu cục + nhân viên) */
export function getJourneyDetail(journey: TrackingJourney): string {
  const parts: string[] = [];

  // Ghi chú phụ (nếu khác tenTrangThai và khác label chính)
  const label = getJourneyLabel(journey);
  if (journey.ghiChu && journey.ghiChu.trim() && journey.ghiChu.trim() !== label) {
    parts.push(journey.ghiChu.trim());
  }

  if (journey.buuCucDi) {
    parts.push(`Bưu cục: ${journey.buuCucDi}${journey.tinhDi ? `, ${journey.tinhDi}` : ''}`);
  }
  if (journey.nguoiTacDong) {
    parts.push(`NV: ${journey.nguoiTacDong}`);
  }
  return parts.join(' · ');
}
